import { CREATURE_BY_ID, CREATURE_TYPES, creatureTone, stateIndex, TONE_COLORS, type CreatureAttack, type CreatureState } from "@eldrun/content";
import { Mesh, PerspectiveCamera, PlaneGeometry, Scene, Vector3, type WebGLRenderer } from "three";
import { skinnedColumn } from "../render/prewarm.ts";
import { clampDelta, FrameTimer } from "../engine/frame-loop.ts";
import { FrameStats } from "../engine/frame-stats.ts";
import { StatsHud } from "../engine/stats-hud.ts";
import { CreatureBars } from "../game/creature-bars.ts";
import { CreatureFxPlayer, hasCreatureFx, planCreatureFx } from "../game/creature-fx.ts";
import { creatureBlob, CreatureLayer, type CreatureLike, type CreatureSnapshot } from "../game/creature-layer.ts";
import { CreatureSkins } from "../game/creature-skins.ts";
import { QUALITY } from "../render/quality.ts";
import { HazardDecals } from "../game/hazards.ts";
import { Lobs } from "../game/lobs.ts";
import { Shards } from "../game/shards.ts";
import { SkillVfx } from "../game/skill-vfx.ts";
import { Smoke } from "../game/smoke.ts";
import { Telegraphs } from "../game/telegraphs.ts";
import { applyAtmosphere, applyToonStyle, createAtmosphereUniforms, MAX_BLOBS, sampleAtmosphere, TOON_STYLE, type ToonStyle } from "../render/atmosphere.ts";
import { BlobShadows } from "../render/blob-shadows.ts";
import { PostFx } from "../render/post/post-fx.ts";
import { countPrograms } from "../render/post/post-model.ts";
import { probeAt, shadingProbe, type ShadingProbeOptions, type ShadingProbeResult } from "../render/shading-probe.ts";
import { toonMaterial } from "../render/shaders/toon.ts";
import { createSurfaceMaterial } from "../render/shaders/surface.ts";
import { gameColor } from "../render/style/roles.ts";

export interface CreatureViewer {
  stats(): Record<string, unknown>;
  /** Sonda de sombreamento (F51): o quadro atual no dia, no crepúsculo e na noite (F52: e pelo pós de um tier). */
  shading(options?: ShadingProbeOptions): ShadingProbeResult;
  /** Manequim com skinning (F51): ossos, morphs e os pixels no meio da parte dobrada e onde ela estaria sem os ossos. */
  skinning(): SkinningProbe;
  /**
   * Chão ao lado de cada criatura, na direção para longe da luz (onde a sombra-blob se alonga), a
   * 0,4, 0,7 e 1 raio da sombra, com as sombras e sem elas (F51): a verdadeira escurece, a cópia do
   * Guardião não.
   */
  blobTell(ids: readonly string[]): BlobTell[];
  stop(): void;
}

/** Pixels RGBA do chão ao lado de uma criatura, com as sombras-blob (`on`) e sem (`off`). */
export interface BlobTell {
  id: string;
  samples: { on: number[] | null; off: number[] | null }[];
}

export interface SkinningProbe {
  bones: number;
  morphs: number;
  /** RGBA no meio da metade de cima, já dobrada pelo osso do meio. */
  bent: number[] | null;
  /** RGBA onde esse ponto ficaria sem o skinning (a coluna reta). */
  rest: number[] | null;
  /** O ponto dobrado na tela (0 a 1, de cima à esquerda), para conferir a captura do canvas. */
  screen: [number, number];
}

/** Opções da vitrine (F50): os modelos animados (`&animadas=0` desliga; um número muda o teto) e a horda. */
export interface CreatureViewerOptions {
  animated: boolean;
  /** Teto de animadas (`QUALITY.animated`). */
  cap?: number;
  /** `&horda=40`: só uma roda de assombrações andando em volta, animadas e procedurais misturadas. */
  horde?: number;
  /** `&lampejos=0`: a assinatura dos inimigos maiores com "Reduzir lampejos" (F91). */
  reduceFlashes?: boolean;
  /** `&foco=maiores`: a câmera perto da fileira dos inimigos maiores (F91), e só eles atacam, para ver a assinatura. */
  focus?: boolean;
}

/**
 * Um golpe de modelo animado medido na vitrine: o quadro em que o clipe passou do impacto contra o
 * acerto anunciado (`impactAt`), em ms, e a duração daquele quadro.
 */
export interface StrikeSample {
  id: string;
  clip: string;
  errorMs: number;
  frameMs: number;
}

/** Ciclo de ataque de cada assombração na vitrine: preparo (com aviso), golpe, recuperação, pausa. */
interface Actor {
  id: string;
  c: CreatureLike;
  attacks: readonly CreatureAttack[];
  next: number;
  step: number;
  until: number;
  attack: CreatureAttack | undefined;
  homeX: number;
  homeZ: number;
}

const flat = () => 0;

/**
 * Vitrine das criaturas (`?debug=criaturas`, sem servidor; `&fase=0.7` mostra a noite). Na frente,
 * a fauna (capivara andando, tatu vagando e enrolando, ema chutando); atrás, as seis assombrações
 * repetindo cada ataque com o aviso no chão, o rastro de fogo, a poça e o caldeirão voando, e um
 * Corpo-Seco reforçado, e um manequim de dois ossos dobrado (o skinning do material, F51). Os inimigos maiores
 * soltam a assinatura deles no golpe (F91: estilhaços, onda, cristais). Serve para ajustar malhas, poses e shaders e para o e2e conferir que tudo
 * compila nos três motores. `frozen` (`&tempo=0`, F51) para o relógio em 0: poses, ataques e
 * shaders ficam iguais a cada quadro (o golden da sonda de sombreamento); `style` (`&estilo=`), o
 * estilo do toon (`&estilo=0`: sem os termos da F51, para comparar).
 */
export function startCreatureViewer(renderer: WebGLRenderer, phase = 0.15, frozen = false, style: ToonStyle = TOON_STYLE, options: CreatureViewerOptions = { animated: true }): CreatureViewer {
  const scene = new Scene();
  const atmosphere = createAtmosphereUniforms();
  applyToonStyle(atmosphere, style);
  const sample = sampleAtmosphere(phase);
  scene.background = sample.horizon;
  const camera = new PerspectiveCamera(55, 1, 0.1, 300);
  camera.position.set(0, 9, 18);
  camera.lookAt(0, 1, -5);
  if (options.focus) {
    camera.position.set(0, 6, -1);
    camera.lookAt(0, 0.6, -11);
  }
  // O chão recebe as sombras-blob (F51), como o terreno do jogo.
  const ground = new Mesh(new PlaneGeometry(80, 80), createSurfaceMaterial({ atmosphere, name: "chao", color: "#7FA34E", blobs: true, normal: "up" }));
  ground.rotation.x = -Math.PI / 2;
  scene.add(ground);
  // Modelos animados (F50): carregam na primeira vez que uma criatura do tipo chega perto.
  const skins = new CreatureSkins(atmosphere);
  // Parada (`&tempo=0`, a sonda de sombreamento), fica toda procedural, como antes.
  skins.enabled = options.animated && !frozen;
  if (options.cap !== undefined) QUALITY.animated = options.cap;
  const layer = new CreatureLayer(scene, atmosphere, skins);
  const strikes: StrikeSample[] = [];
  /** Instante do clipe do golpe de cada criatura no quadro anterior (para achar a passagem pelo impacto). */
  const lastStrike = new Map<string, { clip: string; time: number; impactAt: number }>();
  const blobs = new BlobShadows(atmosphere);
  // Manequim de dois ossos (F51), dobrado no meio: o material de superfície num SkinnedMesh (F49/F50).
  const manequim = skinnedColumn(toonMaterial(atmosphere, "#C9A66B", 1, false, { outline: true }), 2, 0.5);
  manequim.mesh.position.set(-7, 0, 6);
  manequim.joint.rotation.z = -1.1;
  scene.add(manequim.mesh);
  const bars = new CreatureBars(scene);
  const telegraphs = new Telegraphs(scene, atmosphere);
  const hazards = new HazardDecals(scene, atmosphere);
  const lobs = new Lobs(scene, atmosphere);
  const smoke = new Smoke(scene);
  // Assinatura dos inimigos maiores (F91): estilhaços, onda e cristais no tempo do golpe. Parada
  // (`&tempo=0`, o golden) e na horda, nada: a cena fica como antes.
  const fxOn = !frozen && !options.horde;
  const shards = fxOn ? new Shards(scene, () => 0) : undefined;
  const vfx = fxOn ? new SkillVfx(scene) : undefined;
  const fx = shards && vfx ? new CreatureFxPlayer({ shards, vfx, ground: flat }) : undefined;
  const hud = new StatsHud(document.body);
  const timer = new FrameTimer();
  // F52: a contagem do quadro e o pós-processamento (`&pos=`; padrão, o Médio).
  const counter = new FrameStats(renderer.info);
  const unwatch = counter.watch(scene);
  const post = new PostFx(renderer);
  post.bind(atmosphere);
  const forward = new Vector3();
  const resize = () => {
    renderer.setSize(window.innerWidth, window.innerHeight, false);
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
  };
  window.addEventListener("resize", resize);
  resize();

  const make = (type: string, x: number, z: number, state: CreatureState, elite = false): CreatureLike => {
    const def = CREATURE_BY_ID.get(type)!;
    const fly = def.haunt?.fly?.altitude ?? 0;
    // Vida pela metade (menos a ema e as reforçadas) para a barra mostrar o preenchimento.
    const hpMax = def.hp * (elite ? 1.6 : 1);
    return { type, x, y: fly, z, yaw: 0, hp: type === "ema" ? hpMax : Math.round(hpMax * 0.55), hpMax, state: stateIndex(state), elite };
  };
  const creatures = new Map<string, CreatureLike>();
  const actors: Actor[] = [];
  /** Horda (`&horda=`): cada uma num círculo em volta do centro, no seu raio, ângulo e sentido. */
  const horde: { c: CreatureLike; radius: number; angle: number; speed: number }[] = [];
  if (options.horde) {
    const types = CREATURE_TYPES.filter((t) => t.haunt).map((t) => t.id);
    for (let i = 0; i < options.horde; i++) {
      const type = types[i % types.length]!;
      const radius = 5 + (i % 6) * 4.5 + (i % 2) * 1.5;
      const c = make(type, 0, 0, "perseguindo");
      horde.push({ c, radius, angle: (i * 2.399963) % (Math.PI * 2), speed: (CREATURE_BY_ID.get(type)!.speed * (i % 3 === 0 ? 0.5 : 1)) / radius });
      creatures.set(`h${i}`, c);
    }
  } else {
    const fauna = CREATURE_TYPES.filter((t) => t.fauna);
    fauna.forEach((t, i) => creatures.set(t.id, make(t.id, (i - 1) * 4, 3, t.id === "ema" ? "alerta" : "vagando")));
    const haunts = CREATURE_TYPES.filter((t) => t.haunt);
    haunts.forEach((t, i) => {
      // As seis de antes na fila da frente, como sempre; os inimigos maiores (F89) numa fila atrás.
      const back = i >= 6;
      const x = back ? (i - 7) * 5.5 : (i - 2.5) * 4.2;
      const z = back ? -10 : -4;
      const c = make(t.id, x, z, "perseguindo");
      creatures.set(t.id, c);
      // Todas olham para a câmera (yaw π: frente para +z), para os avisos aparecerem inteiros.
      c.yaw = Math.PI;
      actors.push({ id: t.id, c, attacks: t.attacks, next: 0, step: -1, until: 0, attack: undefined, homeX: x, homeZ: z });
    });
    // Chefes (F22) ao fundo, também repetindo cada ataque.
    const bosses = CREATURE_TYPES.filter((t) => t.archetype === "chefe");
    bosses.forEach((t, i) => {
      const x = (i - 1.5) * 7;
      const z = -13;
      const c = make(t.id, x, z, "perseguindo");
      creatures.set(t.id, c);
      c.yaw = Math.PI;
      actors.push({ id: t.id, c, attacks: t.attacks, next: 0, step: -1, until: 0, attack: undefined, homeX: x, homeZ: z });
    });
    // Guardiões (F23) na última fileira; a cópia da Rosa (sem sombra) ao lado da verdadeira.
    const guardians = CREATURE_TYPES.filter((t) => t.archetype === "guardiao");
    guardians.forEach((t, i) => {
      const x = (i - 2) * 5;
      const z = -21;
      const c = make(t.id, x, z, "perseguindo");
      creatures.set(t.id, c);
      c.yaw = Math.PI;
      actors.push({ id: t.id, c, attacks: t.attacks, next: 0, step: -1, until: 0, attack: undefined, homeX: x, homeZ: z });
    });
    creatures.set("copia", { ...make("guardiao-rosa", 13, -21, "perseguindo"), decoy: true, yaw: Math.PI });
  }
  const elite = make("corpo-seco", 9, 3, "perseguindo", true);
  elite.yaw = Math.PI;
  if (!options.horde) creatures.set("reforcado", elite);
  let last = performance.now();
  let lastWhole = 0;
  let frame: readonly CreatureSnapshot[] = [];

  const play = (a: Actor, now: number) => {
    // preparo → golpe → recuperação → pausa, alternando os ataques do tipo.
    if (now < a.until) return;
    a.step = (a.step + 1) % 4;
    if (a.step === 0) {
      a.attack = a.attacks[a.next % a.attacks.length];
      a.next++;
      const att = a.attack!;
      a.c.x = a.homeX;
      a.c.z = a.homeZ;
      a.c.state = stateIndex("preparando");
      a.until = now + att.windup * 1000;
      const point = att.kind === "mergulho" || att.kind === "arremesso" || att.kind === "marcas";
      const tx = a.c.x;
      const tz = a.c.z + (point ? 6 : 0);
      const flight = att.flight ? att.flight[0] * 1000 : 0;
      const width = att.width ?? 0;
      const range = att.shape === "linha" ? Math.min(att.range, 8) : att.range;
      const event = { id: a.id, attack: att.id, shape: att.shape, x: tx, z: tz, yaw: a.c.yaw, range, angle: att.angle, width, inMs: att.windup * 1000 + flight };
      telegraphs.spawn(event, now, 0, flat);
      const def = CREATURE_BY_ID.get(a.id)!;
      if (fx && hasCreatureFx(a.id, att.id)) fx.play(planCreatureFx(event, def, now, 0, { color: gameColor(TONE_COLORS[creatureTone(def.archetype, true)]), reduced: options.reduceFlashes === true, density: fx.density() }));
      // O golpe do modelo animado (F50), com o impacto no acerto do aviso.
      layer.strike({ id: a.id, attack: att.id, inMs: att.windup * 1000 + flight, ...(att.kind === "marcas" && { mark: 0 }) }, now, 0);
      if (att.kind === "arremesso") lobs.spawn({ x: a.c.x, y: 1.6, z: a.c.z }, { x: tx, y: 0, z: tz }, now + att.windup * 1000, now + att.windup * 1000 + flight);
      return;
    }
    const att = a.attack!;
    if (a.step === 1) {
      a.c.state = stateIndex("golpeando");
      a.until = now + 400;
      if (att.hazard?.kind === "fogo") hazards.spawn({ id: `h${now}`, kind: "fogo", x: a.c.x, z: a.c.z, x2: a.c.x, z2: a.c.z + 8, radius: att.hazard.radius, ms: 3000 }, now, flat);
      if (att.hazard?.kind === "poca") hazards.spawn({ id: `h${now}`, kind: "poca", x: a.c.x, z: a.c.z + 6, x2: a.c.x, z2: a.c.z + 6, radius: att.hazard.radius, ms: 3000 }, now + (att.flight ? att.flight[0] * 1000 : 0), flat);
      return;
    }
    if (a.step === 2) {
      a.c.state = stateIndex(att.vulnerable ? "vulneravel" : "recuperando");
      a.until = now + (att.vulnerable ?? att.recovery) * 1000;
      return;
    }
    a.c.state = stateIndex("perseguindo");
    a.until = now + 900;
  };

  renderer.setAnimationLoop((clock: number) => {
    counter.begin();
    const now = frozen ? 0 : clock;
    const dt = frozen ? 0 : clampDelta((now - last) / 1000);
    // O quadro de verdade (o `dt` é limitado a 100 ms: com a máquina lenta, um quadro passa disso).
    const frameMs = clock - last;
    timer.push(frameMs);
    last = clock;
    const t = now / 1000;
    const capivara = creatures.get("capivara");
    if (capivara) {
      capivara.z = 3 + Math.sin(t * 0.8) * 1.2;
      capivara.yaw = Math.cos(t * 0.8) > 0 ? Math.PI : 0;
    }
    const tatu = creatures.get("tatu");
    if (tatu) tatu.state = stateIndex(Math.floor(t / 3) % 2 === 0 ? "vagando" : "enrolado");
    for (const h of horde) {
      h.angle += h.speed * dt;
      h.c.x = Math.cos(h.angle) * h.radius;
      h.c.z = -8 + Math.sin(h.angle) * h.radius;
      // De frente para onde anda (a frente é −z no yaw 0).
      h.c.yaw = Math.atan2(Math.sin(h.angle), -Math.cos(h.angle));
    }
    // Com o foco nos inimigos maiores (F91), só eles atacam: os avisos dos outros não cobrem a assinatura.
    for (const a of actors) if (!options.focus || a.attacks.some((att) => hasCreatureFx(a.id, att.id))) play(a, now);
    // A cada 6 s, o reforçado se desfaz em fumaça e volta (para ver a dissolução).
    const whole = Math.floor(t / 6);
    if (whole !== lastWhole && !options.horde) {
      lastWhole = whole;
      elite.state = stateIndex(whole % 2 === 0 ? "perseguindo" : "dissolvendo");
      if (whole % 2 === 1) smoke.burst(elite.x, 1, elite.z, 10, 0.8);
    }
    applyAtmosphere(atmosphere, sample, t);
    frame = layer.update(creatures, (c) => c, dt, t, camera.position.x, camera.position.z);
    // O quadro em que cada golpe de modelo animado passa do impacto, contra o acerto anunciado.
    for (const a of actors) {
      const info = layer.animated(a.id);
      const strike = info?.strike;
      const sample = strike && info.body.mix?.samples.find((x) => x.clip === strike.clip);
      const before = lastStrike.get(a.id);
      if (strike && sample) {
        if (before && before.impactAt === strike.impactAt && before.time < strike.impact - 1e-6 && sample.time >= strike.impact - 1e-6) {
          strikes.push({ id: a.id, clip: strike.clip, errorMs: now - strike.impactAt, frameMs });
          if (strikes.length > 64) strikes.shift();
        }
        lastStrike.set(a.id, { clip: strike.clip, time: sample.time, impactAt: strike.impactAt });
      } else lastStrike.delete(a.id);
    }
    blobs.begin();
    layer.castBlobs(blobs, flat);
    // A câmera fica a ~25 m das últimas fileiras: todas cabem no alcance.
    blobs.end(camera.position.x, camera.position.z, MAX_BLOBS);
    for (const id of creatures.keys()) bars.markHit(id, now);
    bars.update(frame, camera.position, camera.getWorldDirection(forward), now, dt);
    telegraphs.update(now);
    hazards.update(now);
    lobs.update(now);
    smoke.update(dt);
    fx?.update(now);
    vfx?.update(now);
    shards?.update(dt);
    post.render(scene, camera, dt, counter);
    const f = counter.end();
    hud.update(now, () => [`Eldrun · criaturas · ${timer.fps.toFixed(0)} fps · draw calls ${f.drawCalls} (+${f.postCalls} do pós ${post.plan().tier})`, `fauna na frente · monstros, chefes e Guardiões atrás, repetindo cada ataque · Esqueleto reforçado à direita; cópia do Guardião do Quartzo (sem sombra) no canto`]);
  });

  return {
    stats: () => ({
      fps: timer.fps,
      drawCalls: counter.last.drawCalls,
      frameCalls: counter.last.frameCalls,
      // Programas da cena (F52: os do pós à parte, em `post.programs`).
      programs: countPrograms(renderer.info.programs).scene,
      post: post.stats(counter.last),
      creatures: { ...layer.stats, cap: QUALITY.animated, skins: skins.stats(), bars: bars.visible, telegraphs: telegraphs.count, hazards: hazards.count, lobs: lobs.count, smoke: smoke.count },
      // F91: passos da assinatura disparados, na fila, de fora (fila cheia) e velhos; estilhaços vivos e peças.
      fx: { fired: fx?.fired ?? 0, queued: fx?.queue.count ?? 0, dropped: fx?.queue.dropped ?? 0, stale: fx?.queue.stale ?? 0, shards: shards?.live ?? 0, vfx: vfx?.count ?? 0 },
      // F50: os golpes dos modelos animados medidos (erro do quadro do impacto contra o acerto, ms).
      strikes: [...strikes],
      // Sombras-blob (F51): escolhidas (ids) e quantas cópias do Guardião entre elas (tem de ser 0).
      blobs: (() => {
        const b = blobs.stats();
        return { ...b, decoys: b.ids.filter((id) => layer.decoy(id)).length };
      })(),
    }),
    skinning: () => {
      scene.updateMatrixWorld();
      const bent = manequim.joint.localToWorld(new Vector3(0, 0.8, 0));
      const rest = manequim.mesh.localToWorld(new Vector3(0, 1.8, 0));
      const [a, b] = probeAt(renderer, camera, () => renderer.render(scene, camera), [bent, rest], atmosphere.uOutlineMask);
      const ndc = bent.clone().project(camera);
      return { bones: manequim.mesh.skeleton.bones.length, morphs: Object.keys(manequim.mesh.geometry.morphAttributes).length, bent: a ?? null, rest: b ?? null, screen: [(ndc.x + 1) / 2, (1 - ndc.y) / 2] };
    },
    blobTell: (ids) => {
      const light = atmosphere.uLightDir.value;
      const away = new Vector3(-light.x, 0, -light.z).normalize();
      const steps = [0.4, 0.7, 1];
      const points = ids.flatMap((id) => {
        const c = frame.find((s) => s.id === id);
        // O raio da verdadeira, também para a cópia (o mesmo lugar em volta dos pés).
        const radius = c ? (creatureBlob(c.type, c.state, c.y, c.elite, false, 1, 0)?.radius ?? 0) : 0;
        return steps.map((k) => (c ? new Vector3(c.x, 0, c.z).addScaledVector(away, k * radius) : new Vector3(0, -1000, 0)));
      });
      const render = () => renderer.render(scene, camera);
      const on = probeAt(renderer, camera, render, points, atmosphere.uOutlineMask);
      const strength = atmosphere.uBlobStyle.value.x;
      atmosphere.uBlobStyle.value.x = 0;
      const off = probeAt(renderer, camera, render, points, atmosphere.uOutlineMask);
      atmosphere.uBlobStyle.value.x = strength;
      return ids.map((id, i) => ({ id, samples: steps.map((_, k) => ({ on: on[i * steps.length + k] ?? null, off: off[i * steps.length + k] ?? null })) }));
    },
    shading: (options) => {
      const probed = shadingProbe(
        renderer,
        camera,
        (p, fx) => {
          const s = sampleAtmosphere(p);
          applyAtmosphere(atmosphere, s, 0);
          scene.background = s.horizon;
          if (fx) fx.render(scene, camera, 0);
          else renderer.render(scene, camera);
        },
        options,
        atmosphere,
      );
      scene.background = sample.horizon;
      return probed;
    },
    stop() {
      renderer.setAnimationLoop(null);
      post.dispose();
      unwatch();
      window.removeEventListener("resize", resize);
      layer.dispose();
      skins.dispose();
      bars.dispose();
      telegraphs.dispose();
      hazards.dispose();
      lobs.dispose();
      smoke.dispose();
      vfx?.dispose();
      shards?.dispose();
      fx?.clear();
      ground.geometry.dispose();
      ground.material.dispose();
      manequim.mesh.geometry.dispose();
      manequim.mesh.skeleton.dispose();
      hud.remove();
    },
  };
}
